import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { defineStore } from 'pinia';
import useNumberMoneyFormatting from '~/composables/useNumberMoneyFormatting';
import { useAuthStore } from '~/store/auth';
import { useCurrencyStore } from '~/store/currency';
import {
  closeTicketApi,
  createTicketApi,
  fetchSupportInfosApi,
  fetchTicketByIdApi,
  fetchTicketsApi,
  rateSupportMessageApi,
  sendReplyApi,
} from '~/store/support/api';
import * as helpers from '~/store/support/helpers';
import type { AddonPriceFormatter } from '~/store/support/pricing';
import * as pricingUtils from '~/store/support/pricing';
import type { MessageRating } from '~/store/support/protoHelpers';
import type {
  AddonPrice,
  AddonPricing,
  AddonType,
  CategoryGroup,
  CreateTicketStatus,
  EnhancedAddon,
  Ticket,
  TicketAttachmentUpload,
  TicketCategory,
  TicketMessage,
  UserServer,
} from '~/store/support/types';

// Store definition -----------------------------------------------------------

export const useSupportStore = defineStore('support', () => {
  // State --------------------------------------------------------------------
  const ticketsList = ref<Ticket[]>([]);
  const messagesByTicket = ref<Record<string, TicketMessage[]>>({});

  // Tickets this client put into `ticketsList` itself rather than having seen in a list response:
  // fetched individually by id, or just created. They survive a list refresh that does not contain
  // them, because Intercom's conversation search lags behind writes — so the very ticket a customer
  // is reading, or has just opened, is routinely the one the list does not know about yet.
  const locallyInserted = ref<Set<string>>(new Set());

  const categories = ref<TicketCategory[]>([]);
  const categoryGroups = ref<CategoryGroup[]>([]);

  // Back-compat convenience lists
  const gameCategories = ref<TicketCategory[]>([]);
  const uncategorizedCategories = ref<TicketCategory[]>([]);

  const userServers = ref<UserServer[]>([]);
  const addonPricing = ref<AddonPricing[]>([]); // Global addon pricing (not category-specific)

  const isLoading = ref(false);
  const isDetailLoading = ref(false);
  const isSupportInfosLoading = ref(false);
  const hasPaymentMethod = ref(false);
  const paymentMethodUrl = ref<string>('');

  const authStore = useAuthStore();
  const currencyStore = useCurrencyStore();
  const currentCurrency = computed(() =>
    (currencyStore.currency || '').trim().toUpperCase(),
  );

  // TV-1999: read the composer off the Nuxt app, not `useI18n()`. This store's
  // setup runs from `plugins/auth/useKeycloakAuth.ts` with no component instance,
  // where `useI18n()` throws MUST_BE_CALL_SETUP_TOP - and Pinia caches the
  // half-built store before running setup, so the throw poisons it for the whole
  // page load. `$i18n` is the same composer while vue-i18n is in composition mode.
  const { t } = useNuxtApp().$i18n;
  const { pushErrorNotification } = useNotifications();

  const { format: formatMoney } = useNumberMoneyFormatting();

  // Helpers ------------------------------------------------------------------
  const pushSupportError = (key: string) => {
    pushErrorNotification({
      title: t('general/error/text').toString(),
      message: t(`support/error/${key}`).toString(),
      testId: `support-error-${key}`,
    });
  };

  const formatAddonPrice: AddonPriceFormatter = (price) => {
    if (!price) return undefined;
    return formatMoney(price);
  };

  // Getters ------------------------------------------------------------------

  const getMessages = (id: string): TicketMessage[] =>
    messagesByTicket.value[id] || [];

  // Pricing helpers (delegated to pricing utils)
  const priceByCurrency = (
    map?: Record<string, AddonPrice>,
  ): AddonPrice | null =>
    pricingUtils.priceByCurrency(map, currentCurrency.value);

  const getAddonPricing = (addonType: AddonType): AddonPricing | null =>
    pricingUtils.getAddonPricing(addonPricing.value, addonType);

  const getEffectiveAddonPrice = (addonType: AddonType): AddonPrice | null =>
    pricingUtils.getEffectiveAddonPrice(
      addonPricing.value,
      addonType,
      currentCurrency.value,
    );

  const getAddonDiscountMeta = (
    addonType: AddonType,
  ): {
    discountReasonKey: string;
    remainingFreeTickets?: number;
  } => pricingUtils.getAddonDiscountMeta(addonPricing.value, addonType);

  const buildUpgradeOptions = (
    baseOptions: Array<{
      type: AddonType;
      title: string;
      icon: IconDefinition;
    }>,
    category: TicketCategory | null,
  ): EnhancedAddon[] =>
    pricingUtils.buildUpgradeOptions(
      baseOptions,
      category,
      {
        addonPricing: addonPricing.value,
        currentCurrency: currentCurrency.value,
        uncategorizedCategories: uncategorizedCategories.value,
      },
      formatAddonPrice,
    );

  // Actions ------------------------------------------------------------------

  const fetchTickets = async (): Promise<void> => {
    isLoading.value = true;
    try {
      const tickets = await fetchTicketsApi();

      if (tickets === null) {
        ticketsList.value = [];
        pushSupportError('fetch_tickets');
        return;
      }

      // Merge rather than replace, keeping any ticket a detail fetch inserted that this list does
      // not contain. The two run concurrently on the ticket route — the list from `support:init`,
      // the detail from the route watcher — and Intercom's conversation search lags a few seconds
      // behind writes, so a just-created or just-opened ticket is routinely absent from the list. If
      // the list landed second it dropped that ticket, `activeTicket` resolved to null and the
      // detail panel went blank. Same race the messages map has, same resolution.
      const listed = new Set(tickets.map((ticket) => ticket.id));
      const keptLocal = ticketsList.value.filter(
        (ticket) =>
          locallyInserted.value.has(ticket.id) && !listed.has(ticket.id),
      );
      ticketsList.value = [...keptLocal, ...tickets];

      // Seed the messages map, but NEVER overwrite what is already there.
      //
      // The list is a strictly poorer source than the detail: Intercom's conversation search
      // returns no `conversation_parts`, so a listed ticket carries only the message that opened
      // it, while fetchTicketById returns the whole thread. Both write this map, and on the ticket
      // route they run concurrently — the list from the `support:init` useLazyAsyncData, the detail
      // from the route watcher. Overwriting made that a race whose loser was visible as a thread
      // collapsing to its first message, intermittently, depending on which request landed last.
      //
      // Seeding is still worth doing: it gives the detail view something to render immediately
      // instead of an empty thread while the full fetch is in flight.
      for (const ticket of tickets) {
        if (ticket.messages.length > 0 && !messagesByTicket.value[ticket.id]) {
          messagesByTicket.value[ticket.id] = ticket.messages;
        }
      }
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTicketById = async (id: string): Promise<void> => {
    isDetailLoading.value = true;
    try {
      const ticket = await fetchTicketByIdApi(id);

      if (!ticket) {
        pushSupportError('fetch_ticket_detail');
        return;
      }

      if (ticket.messages.length > 0) {
        messagesByTicket.value[id] = ticket.messages;
      }

      // Marks this ticket as one the LIST must not drop; see fetchTickets.
      locallyInserted.value.add(id);

      const idx = ticketsList.value.findIndex((tick) => tick.id === id);
      if (idx >= 0) {
        ticketsList.value[idx] = ticket;
      } else {
        ticketsList.value.push(ticket);
      }
    } finally {
      isDetailLoading.value = false;
    }
  };

  const sendReply = async (
    id: string,
    body: string,
    attachments: TicketAttachmentUpload[] = [],
  ): Promise<boolean> => {
    const result = await sendReplyApi(id, body, attachments);

    if (result.status !== 'success' || !result.ticket) {
      pushSupportError('send_reply');
      return false;
    }

    const { ticket } = result;

    // Update ticket with new message
    const idx = ticketsList.value.findIndex((tick) => tick.id === id);
    if (idx >= 0) {
      ticketsList.value[idx] = ticket;
    }

    if (ticket.messages.length > 0) {
      messagesByTicket.value[id] = ticket.messages;
    }

    return true;
  };

  const createTicket = async (
    title: string,
    body: string,
    categoryId?: string,
    gameserverId?: string,
    addonType?: AddonType,
  ): Promise<{
    status: CreateTicketStatus;
    ticket?: Ticket;
  }> => {
    const result = await createTicketApi(
      title,
      body,
      categoryId,
      gameserverId,
      addonType,
    );

    if (result.status !== 'success' || !result.ticket) {
      const errorKeyMap: Record<string, string> = {
        'no-payment-method': 'create_ticket_no_payment_method',
        'invalid-argument': 'create_ticket_invalid_argument',
        'payment-authorization-failed':
          'create_ticket_payment_authorization_failed',
        'payment-void-failed': 'create_ticket_payment_void_failed',
      };

      pushSupportError(errorKeyMap[result.status] ?? 'create_ticket');
      return result;
    }

    const { ticket } = result;
    ticketsList.value.unshift(ticket);
    // Same protection as a detail fetch, and more urgently: a list request already in flight when
    // this returned will complete without the new conversation in it, because Intercom's search
    // index has not caught up. Without this the customer watches their new ticket disappear.
    locallyInserted.value.add(ticket.id);

    if (ticket.messages.length > 0) {
      messagesByTicket.value[ticket.id] = ticket.messages;
    }

    return result;
  };

  const rateSupportMessage = async (
    messageId: string,
    rating: MessageRating,
    ticketId?: string,
  ): Promise<'success' | 'already-rated' | 'error'> => {
    const result = await rateSupportMessageApi(messageId, rating);

    if (result.status !== 'success') {
      if (result.status === 'already-rated') {
        pushSupportError('rate_message_already_rated');
      } else {
        pushSupportError('rate_message');
      }
      return result.status;
    }

    // Success - update rating optimistically in store without refetching
    if (ticketId && messagesByTicket.value[ticketId]) {
      const messages = messagesByTicket.value[ticketId];
      const messageIndex = messages.findIndex((m) => m.id === messageId);

      if (messageIndex >= 0) {
        // Create new array with updated message to trigger reactivity
        messagesByTicket.value[ticketId] = messages.map((m, idx) =>
          idx === messageIndex ? { ...m, rating } : m,
        );

        // Also update in ticketsList
        const ticketIndex = ticketsList.value.findIndex(
          (tick) => tick.id === ticketId,
        );
        if (ticketIndex >= 0) {
          const ticket = ticketsList.value[ticketIndex];
          ticketsList.value[ticketIndex] = {
            ...ticket,
            messages: messagesByTicket.value[ticketId],
          };
        }
      }
    }

    return 'success';
  };

  const closeTicket = async (id: string): Promise<boolean> => {
    const result = await closeTicketApi(id);

    if (result.status !== 'success' || !result.ticket) {
      const errorKey =
        result.status === 'payment-void-failed'
          ? 'close_ticket_payment_void_failed'
          : 'close_ticket';
      pushSupportError(errorKey);
      return false;
    }

    const { ticket } = result;

    const idx = ticketsList.value.findIndex((tick) => tick.id === id);
    if (idx >= 0) {
      ticketsList.value[idx] = ticket;
    }
    if (ticket.messages.length > 0) {
      messagesByTicket.value[id] = ticket.messages;
    }

    return true;
  };

  const fetchSupportInfos = async (): Promise<void> => {
    if (!authStore.isAuthenticated) return;

    isSupportInfosLoading.value = true;
    try {
      const infos = await fetchSupportInfosApi();

      if (!infos) {
        pushSupportError('fetch_infos');
        return;
      }

      // Categories
      categories.value = infos.categories;

      // Build category groups using helper
      categoryGroups.value = helpers.buildCategoryGroups(infos.categories);

      // Extract convenience lists using helpers
      gameCategories.value = helpers.extractGameCategories(
        categoryGroups.value,
      );
      uncategorizedCategories.value = helpers.extractUncategorizedCategories(
        categoryGroups.value,
      );

      // User servers
      userServers.value = infos.userServers;

      // Addon pricing
      addonPricing.value = infos.addonPricing;

      // Payment method status
      hasPaymentMethod.value = infos.hasPaymentMethod;
      paymentMethodUrl.value = infos.paymentMethodUrl ?? '';
    } finally {
      isSupportInfosLoading.value = false;
    }
  };

  // Drops every cached message thread without touching categories, servers or pricing.
  //
  // Needed because fetchTickets only SEEDS the message map and never overwrites it (see there), so
  // a caller that invalidates the tickets themselves — a region switch — has to say so explicitly.
  // reset() would do it, but it also clears the support infos the write-ticket form depends on.
  const clearTicketMessages = (): void => {
    messagesByTicket.value = {};
    locallyInserted.value = new Set();
  };

  const reset = (): void => {
    ticketsList.value = [];
    messagesByTicket.value = {};
    locallyInserted.value = new Set();
    categories.value = [];
    categoryGroups.value = [];
    gameCategories.value = [];
    uncategorizedCategories.value = [];
    userServers.value = [];
    addonPricing.value = [];
    isLoading.value = false;
    isDetailLoading.value = false;
    isSupportInfosLoading.value = false;
    hasPaymentMethod.value = false;
    paymentMethodUrl.value = '';
  };

  return {
    // State
    ticketsList,
    messagesByTicket,
    categories,
    categoryGroups,
    gameCategories,
    uncategorizedCategories,
    userServers,
    addonPricing,
    isLoading,
    isDetailLoading,
    isSupportInfosLoading,
    hasPaymentMethod,
    paymentMethodUrl,

    // Getters
    getMessages,
    // Pricing helpers
    priceByCurrency,
    getAddonPricing,
    getEffectiveAddonPrice,
    getAddonDiscountMeta,
    buildUpgradeOptions,

    // Actions
    fetchTickets,
    fetchTicketById,
    sendReply,
    createTicket,
    closeTicket,
    fetchSupportInfos,
    rateSupportMessage,
    reset,
    clearTicketMessages,
  };
});
